import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from 'chart.js';
import packageInfo from '../../package.json';
import { getVersionCommits } from '../services/api';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const MAX_COMMITS = 10;
const MAX_DISTRIBUTION_COMMITS = 100;

function VersionInfoModal({ show, onHide }) {
  const [commitLogs, setCommitLogs] = useState([]);
  const [commitDistributionDetails, setCommitDistributionDetails] = useState([]);
  const [lastCommitDate, setLastCommitDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const version = packageInfo?.version || '';

  useEffect(() => {
    if (show) {
      loadModalData();
    }
  }, [show]);

  const loadModalData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await getVersionCommits();
      const payload = response?.data || {};
      const commitDetailsPayload = Array.isArray(payload.commitDetails) ? payload.commitDetails : [];
      const commitDatesPayload = Array.isArray(payload.commitDates) ? payload.commitDates : [];

      const distributionDetails = commitDetailsPayload
        .map((entry) => ({
          message: entry?.message || '',
          date: entry?.date || ''
        }))
        .filter((entry) => entry.message || entry.date)
        .slice(0, MAX_DISTRIBUTION_COMMITS);

      let logs = Array.isArray(payload.commitLogs) ? payload.commitLogs.slice(0, MAX_COMMITS) : [];

      if (!logs.length && distributionDetails.length) {
        logs = distributionDetails.slice(0, MAX_COMMITS).map((entry) => entry.message).filter(Boolean);
      }

      if (!logs.length && commitDatesPayload.length) {
        logs = commitDatesPayload.slice(0, MAX_COMMITS).map((date, index) => `Commit ${index + 1}`);
      }

      const fallbackDistribution = !distributionDetails.length && logs.length
        ? logs.map((message, index) => ({
            message,
            date: commitDatesPayload[index] || ''
          }))
        : distributionDetails;

      if (!logs.length) {
        throw new Error('No commit history available');
      }

      setLastCommitDate(payload.lastCommitDate || '');
      setCommitLogs(logs);
      setCommitDistributionDetails(fallbackDistribution.slice(0, MAX_DISTRIBUTION_COMMITS));
    } catch (err) {
      setCommitLogs([]);
      setCommitDistributionDetails([]);
      setLastCommitDate('');
      setError('Failed to load commit history');
    } finally {
      setLoading(false);
    }
  };

  const formatDisplayDate = useCallback((rawValue) => {
    if (!rawValue) return '';
    const value = typeof rawValue === 'string' ? rawValue : String(rawValue);
    const isoDateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateOnly) {
      const [, year, month, day] = isoDateOnly;
      return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}`;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return `${parsed.getMonth() + 1}/${parsed.getDate()}/${parsed.getFullYear()}`;
  }, []);

  const headerLabel = () => {
    const parts = [];
    if (version) {
      parts.push(`v${version}`);
    }
    parts.push(`Recent changes (${commitLogs.length})`);

    const formattedDate = formatDisplayDate(lastCommitDate);
    if (formattedDate) {
      parts.push(formattedDate);
    }

    return parts.join(' • ');
  };

  const formatChartAxisLabel = useCallback(
    (isoDate) => {
      const formatted = formatDisplayDate(isoDate);
      if (!formatted) return '';
      const parts = formatted.split('/');
      if (parts.length === 3) {
        return `${parts[0]}/${parts[1]}`;
      }
      return formatted;
    },
    [formatDisplayDate]
  );

  const commitDistribution = useMemo(() => {
    if (!commitDistributionDetails.length) {
      return [];
    }

    const counts = commitDistributionDetails.reduce((acc, detail) => {
      const rawDate = detail?.date || '';
      const normalizedRawDate = typeof rawDate === 'string' ? rawDate : String(rawDate);
      const isoKeyFromSplit = normalizedRawDate.includes('T') ? normalizedRawDate.split('T')[0] : '';
      let normalizedKey = isoKeyFromSplit;

      if (!normalizedKey) {
        const parsed = new Date(normalizedRawDate);
        if (!Number.isNaN(parsed.getTime())) {
          normalizedKey = parsed.toISOString().split('T')[0];
        }
      }

      if (!normalizedKey || !/^\d{4}-\d{2}-\d{2}$/.test(normalizedKey)) {
        return acc;
      }

      acc.set(normalizedKey, (acc.get(normalizedKey) || 0) + 1);
      return acc;
    }, new Map());

    const sortedEntries = Array.from(counts.entries()).sort(
      (a, b) => new Date(`${a[0]}T00:00:00Z`) - new Date(`${b[0]}T00:00:00Z`)
    );

    return sortedEntries.map(([isoDate, count]) => ({
      isoDate,
      label: formatChartAxisLabel(isoDate) || isoDate,
      count
    }));
  }, [commitDistributionDetails, formatChartAxisLabel]);

  const commitsChartData = useMemo(() => {
    if (!commitDistribution.length) {
      return null;
    }

    return {
      labels: commitDistribution.map((item) => item.label),
      datasets: [
        {
          label: 'Commits',
          data: commitDistribution.map((item) => item.count),
          backgroundColor: 'rgba(13, 110, 253, 0.65)',
          borderColor: 'rgba(13, 110, 253, 1)',
          borderWidth: 1,
          borderRadius: 4,
          hoverBackgroundColor: 'rgba(13, 110, 253, 0.8)',
          metaDates: commitDistribution.map((item) => item.isoDate)
        }
      ]
    };
  }, [commitDistribution]);

  const commitsChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 11
            }
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0,
            stepSize: 1
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.08)'
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            title: (items) => {
              if (!items?.length) return '';
              const { dataIndex, dataset } = items[0];
              const isoDate = dataset?.metaDates?.[dataIndex];
              return formatDisplayDate(isoDate) || 'Commits';
            },
            label: (context) => {
              const value =
                typeof context.parsed === 'object' && context.parsed !== null
                  ? context.parsed.y
                  : context.parsed;
              const count = Number.isFinite(value) ? value : 0;
              return `${count} commit${count === 1 ? '' : 's'}`;
            }
          }
        }
      }
    }),
    [formatDisplayDate]
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop={true}
      keyboard={true}
      size="sm"
      dialogClassName="app-version-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100 text-center fw-semibold" style={{ fontSize: '1.2rem' }}>
          {headerLabel()}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-5 py-4">
        {loading ? (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            Loading commit history...
          </div>
        ) : error ? (
          <div className="text-center text-danger py-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        ) : (
          <div className="text-start">
            <ol
              className="mb-4 ps-0"
              style={{ fontSize: '1.05rem', listStylePosition: 'inside' }}
            >
              {commitLogs.map((log, index) => (
                <li
                  key={`${index}-${log}`}
                  className="py-1"
                >
                  {log}
                </li>
              ))}
            </ol>
            {commitsChartData && (
              <div className="mt-4">
                <div className="text-center fw-semibold mb-2" style={{ fontSize: '0.95rem' }}>
                  Commits distribution
                </div>
                <div style={{ height: '200px' }}>
                  <Bar data={commitsChartData} options={commitsChartOptions} />
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default VersionInfoModal;
